import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import ReactDOM from 'react-dom/client';
import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Provider } from './components/ui/provider';
import { StateProvider } from './helpers';
import { installAnalyticsBaseline } from './helpers/utils/analyticsBaseline';
import { initClarity } from './helpers/utils/clarity';
import {
  installErrorReporting,
  onCaughtRenderError,
  reportRouteErrors,
} from './helpers/utils/errorReporting';
import { installScrollRestoration } from './helpers/utils/scrollRestoration';
import { trackClosingKeyboard } from './helpers/utils/closingKeyboard';
import { fontsReady } from './helpers/utils/fontsReady';
import { reloadOnStaleChunk } from './helpers/utils/staleChunkReload';
import { applyUpdatesOnReturn } from './helpers/utils/updateOnReturn';

import './index.css';
import HomePage from './pages/HomePage';
import { loadCalculatorRoute } from './pages/calculatorRoute';
import { Toaster } from './components/ui/toaster';

function CalculatorFallback() {
  return <div role="status" aria-label="Loading game" />;
}

// First, so a crash while the app starts still reaches Clarity
// (errorReporting.ts).
const stopErrorReporting = installErrorReporting();
import.meta.hot?.dispose(stopErrorReporting);

// Take ownership before the first render/load, so native restoration cannot
// apply a second offset after the board appears. The installer waits for the
// document to grow when restoring a position farther down the page.
const uninstallScrollRestoration = installScrollRestoration();
import.meta.hot?.dispose(uninstallScrollRestoration);
// From the start: a name field's keyboard can still be closing when a modal
// opens (closingKeyboard.ts).
const stopClosingKeyboard = trackClosingKeyboard();
import.meta.hot?.dispose(stopClosingKeyboard);
// Before anything loads on demand: a tab from an older deploy reloads into the
// current one instead of going blank (staleChunkReload.ts).
const stopStaleChunkReload = reloadOnStaleChunk();
import.meta.hot?.dispose(stopStaleChunkReload);
// From the start, so opening the app counts as coming back: a newer version
// is applied before the player touches anything, or on their next return
// (updateOnReturn.ts).
const stopUpdatesOnReturn = applyUpdatesOnReturn();
import.meta.hot?.dispose(stopUpdatesOnReturn);

const router = createBrowserRouter([
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: 'spades-calculator',
    // The router loads the board before it leaves Home (calculatorRoute.ts).
    // The fallback only shows on a cold load of the board itself.
    lazy: loadCalculatorRoute,
    hydrateFallbackElement: <CalculatorFallback />,
  },
]);
const stopRouteErrors = reportRouteErrors(router);
import.meta.hot?.dispose(stopRouteErrors);

// Pageview URLs must never carry the viewer `?session=` token: analytics
// would fragment one board into thousands of "unique" URLs and leak session
// IDs into the dashboard. beforeSend rewrites every event to its bare path.
const stripQueryFromUrl = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin + parsed.pathname;
  } catch {
    return url.split('?')[0];
  }
};

export function AppRoot() {
  useEffect(() => {
    initClarity();
    return installAnalyticsBaseline();
  }, []);

  return (
    <Provider>
      <StateProvider>
        <RouterProvider router={router} />
        <Toaster />
        <Analytics
          beforeSend={(event) => ({
            ...event,
            url: stripQueryFromUrl(event.url),
          })}
        />
        <SpeedInsights />
      </StateProvider>
    </Provider>
  );
}

const container = document.getElementById('root');

/** Home's faces, preloaded from index.html (the board's route waits for its
 *  own). */
const HOME_FACES = [
  '400 1em "Roboto Condensed"',
  '700 1em "Roboto Condensed"',
  '800 1em "Roboto Condensed"',
  '400 1em Anton',
  '500 1em Poppins',
];

if (container) {
  const root = ReactDOM.createRoot(container, {
    onCaughtError: onCaughtRenderError,
  });
  // First paint in the real fonts, so nothing reflows when they arrive.
  void fontsReady(HOME_FACES).then(() => root.render(<AppRoot />));
}
